import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Row, Col, Button } from 'reactstrap';

import { formatDate } from '../utils';
import { showError } from '../actions/notificationActions';
import approvals from '../services/approvals.service';

import Loading from './Loading';
import ApprovalStatusBadge from './ApprovalStatusBadge';
import ViewResourceLink from './ViewResourceLink';
import DataTable, { PageHeader } from './DataTable';

const POLL_INTERVAL = 15000;
const PAGE_SIZE = 50;

const safeDate = (d) => (d ? formatDate(d) : '—');

// Maker tracking surface: lists the current user's own requests and refreshes so
// they can watch a request move through pending → executed / denied. No actions
// here — makers cannot approve their own requests.
class MyApprovalRequests extends Component {
  state = {
    requests: [],
    loading: false,
    loaded: false,
  };

  componentDidMount() {
    this.load();
    this.timer = setInterval(() => this.load({ silent: true }), POLL_INTERVAL);
    window.addEventListener('focus', this.onFocus);
  }

  // The user record can land after this mounts (login / session restore); pick
  // up the maker_id as soon as it exists rather than waiting for the next poll.
  componentDidUpdate(prevProps) {
    const prevId = prevProps.user && prevProps.user.id;
    const id = this.props.user && this.props.user.id;
    if (id != null && id !== prevId) this.load();
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
    window.removeEventListener('focus', this.onFocus);
  }

  onFocus = () => this.load({ silent: true });

  load = ({ silent } = {}) => {
    const userId = this.props.user && this.props.user.id;
    if (userId == null) {
      this.setState({ loading: false, loaded: true, requests: [] });
      return;
    }

    if (!silent) this.setState({ loading: true });
    approvals
      // ?mine=true is gone — "my requests" is the ordinary where clause
      // { maker_id: <current user id> }. This is a maker's own tracking view,
      // so one page of the newest requests is all it has ever shown.
      .list({ where: { maker_id: userId }, limit: PAGE_SIZE })
      .then((res) => {
        this.setState({
          loading: false,
          loaded: true,
          requests: res.data || [],
        });
      })
      .catch((err) => {
        this.setState({ loading: false, loaded: true });
        if (!silent) this.props.dispatch(showError(err));
      });
  };

  // Human-readable resolution per row.
  resolution = (r) => {
    if (r.status === 'denied') {
      return r.denial_reason
        ? `Declined: ${r.denial_reason}`
        : `Declined${r.checker ? ` by ${r.checker.name}` : ''}`;
    }
    if (r.status === 'executed') {
      return `Completed${r.checker ? ` by ${r.checker.name}` : ''}`;
    }
    if (r.status === 'failed') {
      return 'Approved but the operation failed';
    }
    if (r.status === 'expired') {
      return 'Expired before approval';
    }
    if (r.checker) {
      return `Picked up by ${r.checker.name}`;
    }
    return 'Awaiting a checker';
  };

  render() {
    const { requests, loading, loaded } = this.state;

    return (
      <div className="container-fluid">
        <Row className="mt-3 mt-md-4">
          <Col xs={12}>
            <PageHeader
              title="My Approval Requests"
              subtitle="Actions you submitted for a second approver"
            >
              <Button
                size="sm"
                color="secondary"
                onClick={() => this.load()}
                disabled={loading}
              >
                {loading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </PageHeader>

            {loading && !loaded && (
              <div className="justify-content-center">
                <Loading />
              </div>
            )}

            <DataTable minWidth={900} loading={loading}>
              <thead className="text-primary">
                <tr>
                  <th>Title</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Resource</th>
                  <th>Resolution</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.reference}>
                    <td>{r.title || '—'}</td>
                    <td>
                      <code>{r.action}</code>
                    </td>
                    <td>
                      <ApprovalStatusBadge status={r.status} />
                    </td>
                    <td>{safeDate(r.created_at)}</td>
                    <td>
                      {r.resource_uri ? (
                        <ViewResourceLink uri={r.resource_uri} />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{this.resolution(r)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(MyApprovalRequests);
